import { useState } from 'react';
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

type FormData = {
  name: string;
  email: string;
  phone: string;
  propertyAddress: string;
  situation: string;
  message: string;
};

const DeterminationOfHeirshipPage = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    situation: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitSuccess(false);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch(
        'https://readdy.ai/api/form/d67c0n7mvg9ih2c793mg',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString(),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Network response was not ok: ${response.status} – ${errorText}`
        );
      }

      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        propertyAddress: '',
        situation: '',
        message: '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Determination of Heirship Texas | Court Proceeding for Intestate Estates | El Paso Guide"
        description="Complete guide to Determination of Heirship in Texas. Learn how this court proceeding identifies legal heirs when someone dies without a will, the process, costs, timeline, and when it is required for transferring inherited property in El Paso."
        keywords="determination of heirship Texas, intestate succession Texas, no will probate, identify legal heirs, El Paso estate law, Texas inheritance without will, heirship proceeding"
        canonical="/elpaso-tx/determination-of-heirship"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline:
            'Determination of Heirship in Texas: Complete Guide for Intestate Estates',
          description:
            'Comprehensive guide to the Determination of Heirship court proceeding in Texas when someone dies without a valid will.',
          author: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
          publisher: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
        }}
      />
      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/dd954606df37bb04eec022457575592f.jpg"
            alt="Texas Probate Court for Determination of Heirship"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/75 to-gray-900/60"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full mb-6">
                Intestate Estate Guide
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Determination of Heirship in Texas:{' '}
                <span className="text-teal-400">
                  The Complete Guide for Intestate Estates
                </span>
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                When a loved one passes away without a will, Texas law
                requires a formal court proceeding to identify the legal heirs.
                This comprehensive guide explains everything you need to know
                about Determination of Heirship, from eligibility and costs to
                the step‑by‑step process.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+1-915-206-5814"
                  className="inline-flex items-center px-6 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call (915) 206‑5814
                </a>
                <a
                  href="/get-cash-offer-for-my-property"
                  className="inline-flex items-center px-6 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get Cash Offer
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Need Help With an Intestate Estate?
              </h3>
              <p className="text-gray-600 mb-6">
                Get expert guidance on your heirship options
              </p>

              {submitSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-teal-600"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">
                    Thank You!
                  </h4>
                  <p className="text-gray-600">
                    We&apos;ll contact you within 24 hours.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="heirship-hero-form"
                  className="space-y-4"
                >
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="text"
                    name="propertyAddress"
                    placeholder="Property Address (if applicable)"
                    value={formData.propertyAddress}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Get Free Consultation'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              Losing a loved one is never easy, and the legal complexities that
              follow can feel overwhelming. When someone passes away in Texas
              without leaving a valid will, their estate is considered
              &quot;intestate,&quot; meaning state law determines who inherits
              their property. But before any assets can be legally transferred
              to the rightful heirs, a formal court proceeding called a{' '}
              <strong>Determination of Heirship</strong> must establish exactly
              who those heirs are and what share each person is entitled to
              receive.
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              This legal process is essential for anyone trying to sell inherited
              real estate, access bank accounts, transfer vehicle titles, or
              distribute any assets from an intestate estate. Without a court
              judgment declaring heirship, title companies will not insure
              property transfers, financial institutions will not release funds,
              and the deceased person&apos;s assets remain in legal limbo. For
              El Paso families dealing with{' '}
              <a
                href="/elpaso-tx/inherited-home-dont-want-it"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                inherited property they may not want to keep
              </a>
              , understanding this process is the critical first step toward
              resolution.
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              Unlike situations where a valid will exists and simplified
              procedures like{' '}
              <a
                href="/elpaso-tx/muniment-of-title"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                Muniment of Title can expedite property transfers
              </a>
              , intestate estates require the court to make a formal
              determination about family relationships and inheritance rights
              based on Texas intestacy statutes. The resulting judgment becomes
              a matter of public record and provides the legal foundation for
              all subsequent property transfers and asset distributions.
            </p>

            <p className="text-gray-700 leading-relaxed">
              This comprehensive guide will walk you through everything you
              need to know about Determination of Heirship proceedings in
              Texas, including when this process is required, the step-by-step
              procedure, typical costs and timelines, how it compares to
              alternatives, and what options you have for inherited property
              once heirship is established. Whether you are dealing with a
              family home, vacant land, or other inherited assets in El Paso,
              this information will help you navigate the path forward with
              confidence.
            </p>
          </div>
        </div>
      </section>

      {/* What Is Determination of Heirship Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What Exactly Is a{' '}
                <span className="text-teal-600">Determination of Heirship</span>?
              </h2>

              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  A Determination of Heirship is a formal court proceeding
                  governed by Chapter 202 of the Texas Estates Code. Its primary
                  purpose is to legally establish the identity of a deceased
                  person&apos;s heirs when that person died without a valid will
                  (intestate) or when an existing will does not dispose of all
                  the deceased&apos;s property. The court examines evidence
                  about the deceased&apos;s family relationships and issues a
                  judgment declaring who the legal heirs are and what
                  percentage of the estate each heir is entitled to receive under
                  Texas law.
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  This proceeding is fundamentally different from probating a
                  will. When someone dies with a valid will, the document
                  itself specifies who receives what, and the court&apos;s role
                  is primarily to validate the will and oversee its execution.
                  But when there is no will, Texas intestacy laws create a
                  default distribution scheme based on family relationships,
                  and the Determination of Heirship proceeding applies these
                  laws to the specific facts of the deceased&apos;s family
                  situation.
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  The judgment issued by the court serves as official,
                  legally binding proof of heirship that can be recorded in
                  county deed records and used to transfer property titles.
                  Title insurance companies, banks, brokerage firms, and other
                  institutions rely on this court judgment to verify they are
                  dealing with legitimate heirs who have legal authority over the
                  deceased&apos;s assets. Without this judgment, most
                  institutions will refuse to transfer assets or insure
                  property titles.
                </p>

                <p className="text-gray-700 leading-relaxed">
                  It is important to understand that a Determination of Heirship
                  can be filed either as an independent proceeding or as part of
                  a larger probate administration. When filed independently, it
                  simply establishes who the heirs are without appointing an
                  administrator to manage the estate. This independent approach
                  is often sufficient when the primary goal is to transfer
                  specific assets like real estate to the identified heirs,
                  particularly for families looking to{' '}
                  <a
                    href="/elpaso-tx/sell-house-fast"
                    className="text-teal-600 hover:text-teal-700 font-semibold"
                  >
                    sell an inherited house quickly in El Paso
                  </a>
                  .
                </p>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=detailed%20genealogy%20family%20tree%20on%20large%20paper%20with%20multiple%20generations%20connected%20by%20lines%20showing%20inheritance%20paths%20official%20court%20documents%20with%20notary%20stamps%20and%20legal%20seals%20spread%20on%20mahogany%20desk%20warm%20golden%20afternoon%20lighting%20professional%20estate%20planning%20photography&width=700&height=500&seq=heirship-what-v2-002&orientation=landscape"
                alt="Family Tree for Determination of Heirship"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* When Is It Required Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              When Is Determination of Heirship{' '}
              <span className="text-teal-600">Required</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding when this court proceeding is necessary helps you
              plan the right approach for your specific situation and avoid
              costly delays.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-forbid-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                No Will Was Ever Created
              </h3>
              <p className="text-gray-600 leading-relaxed">
                The most common scenario requiring Determination of Heirship
                is when the deceased person died without ever creating a will.
                Texas intestacy laws will determine inheritance, but the court
                must formally identify all legal heirs before any assets can be
                transferred.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-damage-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Will Cannot Be Probated
              </h3>
              <p className="text-gray-600 leading-relaxed">
                If a will exists but cannot be admitted to probate because it
                does not meet legal requirements, was improperly executed, or
                cannot be located, the estate is treated as intestate and
                requires a Determination of Heirship.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Four-Year Probate Deadline Passed
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Texas law requires wills to be probated within four years of
                death. If this deadline passes without probate, even a valid
                will cannot be probated, and the estate must proceed through
                Determination of Heirship instead.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Real Estate Not Covered by Will
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Sometimes a will disposes of some assets but fails to address
                certain property, particularly real estate. A Determination of
                Heirship may be needed to establish ownership of property not
                covered by the will.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-cross-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Title Insurance Requirements
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Title companies often require a court judgment of heirship
                before they will insure property transfers from intestate
                estates. Even if heirs agree on distribution, the title company
                may demand this formal proceeding.
              </p>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-colors">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-group-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Complex Family Situations
              </h3>
              <p className="text-gray-600 leading-relaxed">
                When family relationships are complicated by multiple marriages,
                children from different relationships, disputed paternity, or
                unknown heirs, a formal court determination provides legal
                certainty about inheritance rights.
              </p>
            </div>
          </div>

          <div className="bg-teal-50 border-l-4 border-teal-500 rounded-r-xl p-6">
            <div className="flex items-start">
              <i className="ri-lightbulb-line text-2xl text-teal-600 mr-4 mt-1"></i>
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">
                  Simpler Alternative for Some Estates
                </h4>
                <p className="text-gray-700">
                  For some intestate estates with straightforward family
                  situations, an{' '}
                  <a
                    href="/elpaso-tx/affidavit-of-heirship"
                    className="text-teal-600 hover:text-teal-700 font-semibold"
                  >
                    Affidavit of Heirship may provide a simpler and less
                    expensive alternative
                  </a>{' '}
                  to a full court proceeding. However, Affidavits of Heirship
                  are not always accepted by title companies and may not be
                  suitable for complex family situations, disputed estates, or
                  high‑value properties.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Texas Intestacy Laws Determine Heirs */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=texas%20state%20capitol%20building%20with%20scales%20of%20justice%20in%20foreground%20legal%20documents%20showing%20family%20relationships%20and%20inheritance%20distribution%20charts%20warm%20golden%20hour%20lighting%20professional%20legal%20photography%20representing%20state%20intestacy%20laws&width=700&height=500&seq=heirship-intestacy-003&orientation=landscape"
                alt="Texas Intestacy Laws"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                How{' '}
                <span className="text-teal-600">
                  Texas Intestacy Laws Determine Your Heirs
                </span>
              </h2>

              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  The Determination of Heirship proceeding applies{' '}
                  <a
                    href="/elpaso-tx/texas-intestacy-laws"
                    className="text-teal-600 hover:text-teal-700 font-semibold"
                  >
                    Texas intestacy laws to identify who qualifies
                  </a>{' '}
                  as legal heirs and what percentage each heir receives. These
                  laws create a hierarchy of inheritance based on family
                  relationships, with closer relatives taking priority over more
                  distant ones.
                </p>

                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">
                    If Deceased Was Married
                  </h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                      <span>
                        <strong>Community property</strong> goes entirely to the
                        surviving spouse if all children are also children of
                        the surviving spouse
                      </span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                      <span>
                        If deceased had children from another relationship,
                        surviving spouse gets 1/3 of separate personal property
                        and a life estate in 1/3 of separate real property
                      </span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                      <span>
                        Understanding{' '}
                        <a
                          href="/elpaso-tx/texas-community-property-laws"
                          className="text-teal-600 hover:text-teal-700 font-semibold"
                        >
                          Texas community property laws affect inheritance
                        </a>{' '}
                        distribution significantly
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">
                    If Deceased Was Not Married
                  </h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                      <span>
                        <strong>Children</strong> inherit everything equally
                      </span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                      <span>
                        If no children, <strong>parents</strong> inherit
                        everything
                      </span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                      <span>
                        If no parents, <strong>siblings</strong> inherit equally
                      </span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                      <span>
                        More distant relatives inherit only if no closer
                        relatives exist
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Step-by-Step Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              The <span className="text-teal-600">Step-by-Step Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding the Determination of Heirship process helps you
              prepare and know what to expect at each stage.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start mb-12">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=probate%20attorney%20meeting%20with%20family%20members%20around%20conference%20table%20reviewing%20legal%20documents%20and%20family%20tree%20charts%20warm%20professional%20office%20lighting%20showing%20consultation%20and%20guidance%20professional%20legal%20photography&width=700&height=500&seq=heirship-process-004&orientation=landscape"
                alt="Heirship Process Consultation"
                className="w-full h-auto rounded-xl shadow-lg sticky top-8"
              />
            </div>

            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-teal-600">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Gather Required Documents
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Collect the deceased&apos;s death certificate, birth
                      certificates of potential heirs, marriage certificates,
                      divorce decrees, property deeds, and any other documents
                      establishing family relationships and asset ownership.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-teal-600">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      File Application with Probate Court
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      An interested party (usually an heir or creditor) files an
                      Application for Determination of Heirship with the probate
                      court in the county where the deceased resided. The
                      application must include detailed information about the
                      deceased and all potential heirs.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-teal-600">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Court Appoints Attorney Ad Litem
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      The court appoints an independent attorney ad litem to
                      investigate the family history, verify the information in
                      the application, locate unknown heirs, and report findings
                      to the court. This attorney represents the interests of
                      unknown or missing heirs.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-teal-600">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Notice to Potential Heirs
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      All known heirs must be formally notified of the
                      proceeding. If heirs cannot be located, notice may be
                      published in a local newspaper. This ensures everyone with
                      a potential claim has an opportunity to participate.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-teal-600">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                    5
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Court Hearing
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      A hearing is held where the court reviews evidence,
                      examines witnesses who can testify about family
                      relationships, considers the attorney ad litem&apos;s
                      report, and determines the legal heirs and their respective
                      shares of the estate.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-teal-600">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                    6
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Judgment and Recording
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      The court issues a Judgment Declaring Heirship that
                      identifies all heirs and their percentage interests. This
                      judgment is recorded in the county deed records and serves
                      as legal proof of ownership for property transfers.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-teal-600">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                    7
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Transfer Assets to Heirs
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Once the judgment is recorded, heirs can use it to
                      transfer property titles, access financial accounts, and
                      distribute estate assets. For families looking to sell,{' '}
                      <a
                        href="/elpaso-tx/cash-home-buyers-el-paso"
                        className="text-teal-600 hover:text-teal-700 font-semibold"
                      >
                        cash buyers who specialize in properties with title
                        complications
                      </a>{' '}
                      can often close quickly once heirship is established.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost and Timeline Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              <span className="text-teal-600">Cost and Timeline</span>
            </h2>
            <p className="text-xl text-gray-600">
              Understanding the financial and time investment helps you plan
              accordingly
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-teal-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">
                      Expense Category
                    </th>
                    <th className="px-6 py-4 text-left font-semibold">
                      Typical Range
                    </th>
                    <th className="px-6 py-4 text-left font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      Attorney Fees
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      $3,000 – $7,000+
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      Varies based on complexity and attorney rates
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      Attorney Ad Litem
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      $1,500 – $3,000
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      Court-appointed investigator fees
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      Court Filing Fees
                    </td>
                    <td className="px-6 py-4 text-gray-700">$300 – $500</td>
                    <td className="px-6 py-4 text-gray-600">
                      Varies by county
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      Publication Costs
                    </td>
                    <td className="px-6 py-4 text-gray-700">$200 – $400</td>
                    <td className="px-6 py-4 text-gray-600">
                      If notice must be published
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      Recording Fees
                    </td>
                    <td className="px-6 py-4 text-gray-700">$50 – $100</td>
                    <td className="px-6 py-4 text-gray-600">
                      To record judgment in deed records
                    </td>
                  </tr>
                  <tr className="bg-teal-50">
                    <td className="px-6 py-4 font-bold text-gray-900">
                      Total Estimated Cost
                    </td>
                    <td className="px-6 py-4 font-bold text-teal-600">
                      $5,000 – $11,000+
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      Complex cases cost more
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Timeline Expectations
            </h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <i className="ri-time-line text-2xl text-teal-600 mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">
                    Uncontested Cases
                  </h4>
                  <p className="text-gray-700">
                    Typically 3–6 months from filing to final judgment when all
                    heirs agree and family relationships are straightforward
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-time-line text-2xl text-teal-600 mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">
                    Contested or Complex Cases
                  </h4>
                  <p className="text-gray-700">
                    Can take 6–12 months or longer if there are disputes about
                    heirship, missing heirs, or complicated family situations
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-time-line text-2xl text-teal-600 mr-4 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">
                    Court Scheduling
                  </h4>
                  <p className="text-gray-700">
                    Timeline depends heavily on court docket availability and
                    how quickly the attorney ad litem completes their
                    investigation
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison with Alternatives Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comparing Your{' '}
              <span className="text-teal-600">Estate Settlement Options</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding how Determination of Heirship compares to other
              options helps you choose the right approach
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl p-8 border-2 border-gray-200">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-list-3-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Affidavit of Heirship
              </h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    <strong>Cost:</strong> $500 – $1,500
                  </span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    <strong>Time:</strong> 1–2 weeks
                  </span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    No court proceeding required
                  </span>
                </div>
                <div className="flex items-start">
                  <i className="ri-close-line text-red-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    Not always accepted by title companies
                  </span>
                </div>
                <div className="flex items-start">
                  <i className="ri-close-line text-red-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    Not suitable for complex situations
                  </span>
                </div>
              </div>
              <a
                href="/elpaso-tx/affidavit-of-heirship"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                Learn More →
              </a>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 border-2 border-gray-200">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-text-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Small Estate Affidavit
              </h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    <strong>Cost:</strong> $500 – $1,000
                  </span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    <strong>Time:</strong> 30+ days waiting period
                  </span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    Simplified court filing
                  </span>
                </div>
                <div className="flex items-start">
                  <i className="ri-close-line text-red-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    Only for estates under $75,000
                  </span>
                </div>
                <div className="flex items-start">
                  <i className="ri-close-line text-red-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    Excludes homestead and exempt property
                  </span>
                </div>
              </div>
              <a
                href="/elpaso-tx/small-estate-affidavit"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                Learn More →
              </a>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 border-2 border-gray-200">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-government-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Full Probate Administration
              </h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    <strong>Cost:</strong> $5,000 – $15,000+
                  </span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    <strong>Time:</strong> 6–12+ months
                  </span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    Appoints administrator to manage estate
                  </span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    Handles debts and creditor claims
                  </span>
                </div>
                <div className="flex items-start">
                  <i className="ri-close-line text-red-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">
                    Most expensive and time-consuming
                  </span>
                </div>
              </div>
              <a
                href="/elpaso-tx/texas-probate-guide"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                Learn More →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Common Challenges Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Common <span className="text-teal-600">Challenges</span> in
              Heirship Proceedings
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Being aware of potential complications helps you prepare and avoid
              delays
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=concerned%20family%20members%20sitting%20around%20dining%20table%20discussing%20inheritance%20issues%20with%20legal%20documents%20spread%20out%20showing%20disagreement%20and%20complexity%20warm%20natural%20lighting%20professional%20photography%20representing%20family%20estate%20challenges&width=700&height=500&seq=heirship-challenges-005&orientation=landscape"
                alt="Family Discussing Heirship Challenges"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 border-l-4 border-red-500">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-alert-line text-red-500 mr-2"></i>
                  Missing or Unknown Heirs
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  When potential heirs cannot be located or their existence is
                  uncertain, the attorney ad litem must conduct extensive
                  searches, which increases costs and delays the proceeding.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border-l-4 border-red-500">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-alert-line text-red-500 mr-2"></i>
                  Disputed Paternity or Family Relationships
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Questions about biological relationships may require DNA
                  testing or extensive genealogical research, adding complexity
                  and expense to the proceeding.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border-l-4 border-red-500">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-alert-line text-red-500 mr-2"></i>
                  Incomplete or Missing Records
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Lack of birth certificates, marriage licenses, or other vital
                  records can make it difficult to prove family relationships and
                  may require alternative forms of evidence.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border-l-4 border-red-500">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  <i className="ri-alert-line text-red-500 mr-2"></i>
                  Property in Poor Condition
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  If the inherited property is a{' '}
                  <a
                    href="/elpaso-tx/sell-hoarder-houses"
                    className="text-teal-600 hover:text-teal-700 font-semibold"
                  >
                    hoarded property that needs extensive cleanup
                  </a>{' '}
                  or has{' '}
                  <a
                    href="/elpaso-tx/cant-afford-repairs-to-sell-house"
                    className="text-teal-600 hover:text-teal-700 font-semibold"
                  >
                    significant repair needs that would be costly
                  </a>
                  , heirs may struggle to sell through traditional channels even
                  after heirship is established.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-teal-600">Questions</span>
            </h2>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I sell inherited property before completing Determination of
                Heirship?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                No. You cannot legally transfer property title until the court
                issues a judgment declaring heirship. Title companies will not
                insure the transaction without this court judgment. However, you
                can begin the sales process and accept offers contingent on
                completing the heirship proceeding.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What if one heir refuses to participate in the proceeding?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                The proceeding can move forward even if some heirs do not
                participate. As long as proper notice is given to all known
                heirs, the court can issue a judgment binding on all parties. The
                non-participating heir&apos;s share will still be determined
                according to Texas law.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Do all heirs have to agree to sell the property?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Generally yes, unless the court orders a partition sale. When
                multiple heirs inherit property, all must agree to sell or one
                heir can file a partition lawsuit forcing a sale. Working with
                cash buyers who understand these situations can help facilitate
                sales when not all heirs agree on the property&apos;s future.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How long do I have to file for Determination of Heirship?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                There is no strict deadline for filing a Determination of
                Heirship. However, the four-year deadline for probating a will
                applies, after which even a valid will cannot be probated and you
                must proceed with Determination of Heirship instead.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can Determination of Heirship be done without an attorney?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                While technically possible, it is strongly not recommended.
                Heirship proceedings involve complex legal requirements, court
                procedures, and evidentiary standards. Mistakes can result in an
                invalid judgment that does not protect property transfers. Most
                courts expect professional legal representation.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What happens if new heirs are discovered after the judgment?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                If a previously unknown heir comes forward after the judgment is
                issued, they may be able to challenge the judgment within certain
                time limits. This is why the attorney ad litem&apos;s thorough
                investigation is so important. Proper notice and diligent
                searching help prevent these situations.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Does Determination of Heirship address estate debts?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                A standalone Determination of Heirship proceeding only
                establishes who the heirs are; it does not address creditor
                claims or estate debts. If the estate has significant debts, a
                full probate administration may be necessary to properly handle
                creditor claims.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I use Determination of Heirship for property in multiple
                counties?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                The proceeding is filed in the county where the deceased resided.
                The resulting judgment can be recorded in deed records of any
                county where the deceased owned property, making it effective for
                transferring property in multiple locations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Can Help Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=happy%20family%20shaking%20hands%20with%20real%20estate%20professional%20after%20successful%20inherited%20property%20sale%20southwestern%20style%20home%20in%20background%20el%20paso%20texas%20desert%20landscape%20warm%20golden%20hour%20lighting%20showing%20relief%20and%20satisfaction%20professional%20photography&width=700&height=500&seq=heirship-help-006&orientation=landscape"
                alt="Successful Inherited Property Sale"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                How <span className="text-teal-600">TREX RE LLC</span> Can Help
              </h2>

              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  At TREX RE LLC, we understand that navigating Determination of
                  Heirship proceedings while dealing with inherited property can
                  be overwhelming. We specialize in helping El Paso families sell
                  inherited properties quickly and fairly, even when title issues
                  or heirship complications exist.
                </p>

                <div className="bg-white rounded-xl p-6 mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    We Can Buy Your Inherited Property:
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                      <span className="text-gray-700">
                        <strong>During or after heirship proceedings</strong> –
                        We can work with your timeline
                      </span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                      <span className="text-gray-700">
                        <strong>In any condition</strong> – No repairs, cleanup,
                        or improvements needed
                      </span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                      <span className="text-gray-700">
                        <strong>With multiple heirs</strong> – We handle complex
                        ownership situations
                      </span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                      <span className="text-gray-700">
                        <strong>Fast closing</strong> – Close in as little as 7
                        days once heirship is established
                      </span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                      <span className="text-gray-700">
                        <strong>No commissions or fees</strong> – You keep the
                        full offer amount
                      </span>
                    </li>
                  </ul>
                </div>

                <p className="text-gray-700 leading-relaxed mb-6">
                  Whether you are just beginning the Determination of Heirship
                  process or have already received your court judgment, we can
                  provide a fair cash offer and guide you through the sale
                  process. We have experience working with inherited properties,
                  title issues, and multiple heir situations.
                </p>

                <div className="flex flex-wrap gap-4">
                  <a
                    href="tel:+1-915-206-5814"
                    className="inline-flex items-center px-6 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Call (915) 206‑5814
                  </a>
                  <a
                    href="/get-cash-offer-for-my-property"
                    className="inline-flex items-center px-6 py-4 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Get Your Cash Offer
                    <i className="ri-arrow-right-line ml-2"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Resources Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Related <span className="text-teal-600">Resources</span>
            </h2>
            <p className="text-xl text-gray-600">
              Explore more guides to help with your inherited property situation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a
              href="/elpaso-tx/texas-probate-guide"
              className="group bg-gray-50 rounded-xl p-6 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <div className="relative h-40 mb-4 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=texas%20courthouse%20interior%20with%20wooden%20judge%20bench%20legal%20documents%20gavel%20warm%20lighting%20marble%20columns%20showing%20justice%20and%20law%20professional%20legal%20photography&width=400&height=300&seq=probate-guide-resource-007&orientation=landscape"
                  alt="Texas Probate Guide"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600">
                Texas Probate Crash Course
              </h3>
              <p className="text-gray-600 text-sm">
                Complete guide to probate in Texas
              </p>
            </a>

            <a
              href="/elpaso-tx/small-estate-affidavit"
              className="group bg-gray-50 rounded-xl p-6 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <div className="relative h-40 mb-4 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=notary%20stamp%20on%20small%20estate%20affidavit%20document%20with%20calculator%20showing%20seventy%20five%20thousand%20dollars%20family%20photographs%20and%20legal%20papers%20on%20organized%20desk%20warm%20lighting%20professional%20photography&width=400&height=300&seq=small-estate-resource-008&orientation=landscape"
                  alt="Small Estate Affidavit Guide"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600">
                Small Estate Affidavit Guide
              </h3>
              <p className="text-gray-600 text-sm">
                Skip probate for estates under $75K
              </p>
            </a>

            <a
              href="/inherited-homes"
              className="group bg-gray-50 rounded-xl p-6 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <div className="relative h-40 mb-4 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=southwestern%20style%20inherited%20family%20home%20with%20keys%20and%20estate%20documents%20on%20table%20warm%20natural%20lighting%20showing%20property%20inheritance%20professional%20real%20estate%20photography&width=400&height=300&seq=inherited-homes-resource-009&orientation=landscape"
                  alt="Selling Inherited Homes"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600">
                Selling Inherited Homes
              </h3>
              <p className="text-gray-600 text-sm">
                Your options for inherited property
              </p>
            </a>

            <a
              href="/elpaso-tx"
              className="group bg-gray-50 rounded-xl p-6 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <div className="relative h-40 mb-4 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=stack%20of%20real%20estate%20guides%20and%20resource%20books%20on%20wooden%20desk%20with%20el%20paso%20texas%20skyline%20and%20franklin%20mountains%20visible%20through%20window%20warm%20afternoon%20lighting%20professional%20photography&width=400&height=300&seq=resources-center-010&orientation=landscape"
                  alt="El Paso Real Estate Resources"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600">
                More El Paso Resources
              </h3>
              <p className="text-gray-600 text-sm">
                Browse all our local guides
              </p>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DeterminationOfHeirshipPage;
